import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, CheckCircle2, Clock, AlertTriangle, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { StatCard } from "../components/shared";
import EntityDrawer from "../components/EntityDrawer";
import { FileUploader, FileChip } from "../components/module-widgets";

const RECORD_FIELDS = [
  { key: "employee_name", label: "Employee", type: "text" },
  { key: "course", label: "Course", type: "text" },
  { key: "expiry_date", label: "Expiry date", type: "date" },
  { key: "status", label: "Status", type: "select", options: [{ value: "valid", label: "Valid" }, { value: "expiring", label: "Expiring" }, { value: "expired", label: "Expired" }] },
];
const REQUEST_FIELDS = [
  { key: "employee_name", label: "Employee", type: "text" },
  { key: "course", label: "Course", type: "text" },
  { key: "status", label: "Status", type: "select", options: [{ value: "requested", label: "Requested" }, { value: "approved", label: "Approved" }, { value: "completed", label: "Completed" }] },
];

const badge = (s) => ({
  valid: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  expiring: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  expired: "bg-red-500/10 text-red-600 dark:text-red-400",
  requested: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  approved: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
}[s] || "bg-secondary text-muted-foreground");

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "valid", label: "Valid" },
  { value: "expiring", label: "Expiring" },
  { value: "expired", label: "Expired" },
];

export default function Training() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});
  const [sel, setSel] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const [s, r, q] = await Promise.all([api.get("/training/summary"), api.get("/training/records"), api.get("/training/requests")]);
    setSummary(s.data); setRecords(r.data); setRequests(q.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (dlg === "record") {
        const expiry = form.expiry_date ? new Date(form.expiry_date) : null;
        const status = expiry ? (expiry < new Date() ? "expired" : ((expiry - new Date()) / 86400000 < 60 ? "expiring" : "valid")) : "valid";
        await api.post("/training/records", { ...form, expiry_date: expiry ? expiry.toISOString() : null, status });
      } else {
        await api.post("/training/requests", { ...form, status: "requested", requested_by: "You" });
      }
      toast.success("Saved"); setDlg(null); setForm({}); load();
    } catch { toast.error("Failed to save"); }
  };

  const approve = async (id) => { await api.patch(`/training/requests/${id}`, { status: "approved" }); load(); };

  if (!summary) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const filteredRecords = statusFilter === "all" ? records : records.filter((r) => r.status === statusFilter);

  return (
    <div>
      <PageHeader title="Training & Competency" subtitle="Training matrix, certificate expiry tracking and training requests." />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={CheckCircle2} label="Valid certificates" value={summary.valid} tone="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={Clock} label="Expiring soon" value={summary.expiring} tone="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
        <StatCard icon={AlertTriangle} label="Expired" value={summary.expired} tone="bg-red-500/10 text-red-600 dark:text-red-400" />
        <StatCard icon={GraduationCap} label="Open requests" value={summary.requests_open} />
      </div>

      <Tabs defaultValue="matrix">
        <TabsList className="rounded-full">
          <TabsTrigger value="matrix" className="rounded-full" data-testid="training-tab-matrix">Training Matrix</TabsTrigger>
          <TabsTrigger value="requests" className="rounded-full" data-testid="training-tab-requests">Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="matrix" className="mt-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" data-testid="training-status-filter">
              {STATUS_FILTERS.map((f) => {
                const count = f.value === "all" ? records.length : records.filter((r) => r.status === f.value).length;
                const active = statusFilter === f.value;
                return (
                  <button key={f.value} type="button" onClick={() => setStatusFilter(f.value)} data-testid={`training-filter-${f.value}`}
                    className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${active ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                    {f.label} <span className="ml-1 text-xs opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
            <Button className="rounded-full" onClick={() => { setForm({}); setDlg("record"); }} data-testid="new-record-btn"><Plus className="mr-2 h-4 w-4" /> Add Record</Button>
          </div>
          <Card className="overflow-hidden rounded-2xl border-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Employee</th><th className="px-4 py-3 font-medium">Course</th>
                  <th className="px-4 py-3 font-medium">Expiry</th><th className="px-4 py-3 font-medium">Status</th></tr></thead>
                <tbody>
                  {filteredRecords.map((r) => (
                    <tr key={r.id} onClick={() => setSel({ type: "record", item: r })} data-testid={`record-row-${r.id}`}
                      className="cursor-pointer border-b border-border/60 transition-colors hover:bg-secondary/50">
                      <td className="px-4 py-3 font-medium">{r.employee_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.course}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3"><Badge className={`rounded-full capitalize ${badge(r.status)}`}>{r.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredRecords.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground" data-testid="training-empty">No training records match this filter.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <div className="mb-3 flex justify-end"><Button className="rounded-full" onClick={() => { setForm({}); setDlg("request"); }} data-testid="new-request-btn"><Plus className="mr-2 h-4 w-4" /> Request Training</Button></div>
          <Card className="overflow-hidden rounded-2xl border-border">
            {requests.map((q) => (
              <div key={q.id} onClick={() => setSel({ type: "request", item: q })} data-testid={`request-row-${q.id}`}
                className="flex cursor-pointer items-center justify-between border-b border-border/60 px-5 py-3.5 transition-colors last:border-0 hover:bg-secondary/50">
                <div><div className="text-sm font-medium">{q.course}</div><div className="text-xs text-muted-foreground">For {q.employee_name}</div></div>
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-full capitalize ${badge(q.status)}`}>{q.status}</Badge>
                  {q.status === "requested" && <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={(e) => { e.stopPropagation(); approve(q.id); }}>Approve</Button>}
                </div>
              </div>
            ))}
            {requests.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No training requests yet.</div>}
          </Card>
        </TabsContent>
      </Tabs>

      {sel && (
        <EntityDrawer open={!!sel} onClose={() => setSel(null)}
          title={sel.type === "record" ? `${sel.item.course} — ${sel.item.employee_name}` : sel.item.course}
          collection={sel.type === "record" ? "training_records" : "training_requests"}
          patchBase={sel.type === "record" ? "/training/records" : "/training/requests"}
          fields={sel.type === "record" ? RECORD_FIELDS : REQUEST_FIELDS} item={sel.item} onSaved={() => load()}
          renderExtra={sel.type === "record" ? (state, setState) => (
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Certificate</div>
              {state.certificate && <div className="mb-2"><FileChip file={state.certificate} /></div>}
              <FileUploader label="Upload certificate" testid="upload-certificate" onUploaded={(f) => setState((s) => ({ ...s, certificate: f }))} />
            </div>
          ) : undefined} />
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{dlg === "record" ? "Add Training Record" : "Request Training"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Employee</Label><Input value={form.employee_name || ""} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} data-testid="training-employee" /></div>
            <div className="space-y-1.5"><Label>Course</Label><Input value={form.course || ""} onChange={(e) => setForm({ ...form, course: e.target.value })} data-testid="training-course" /></div>
            {dlg === "record" && <div className="space-y-1.5"><Label>Expiry date</Label><Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="training-expiry" /></div>}
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="training-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
